import React, { useEffect, useMemo, useRef } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { Redirect, useGlobalSearchParams, usePathname } from 'expo-router';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { BlankScreen } from 'components/blank-screen';
import { CardModal, CardModalProps } from 'components/card-modal';
import { useGetModalDismiss } from 'components/card-modal-header';
import LoadingContainer from 'components/loading-animation/loading-container';
import { Box } from 'components/ui';
import { useModalFocusTrap } from 'hooks/accessibility/use-modal-focus-trap';
import useEffectOnce from 'hooks/use-effect-once';
import { routes as appRoutes } from 'navigation/routing';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import NotFound from 'pages/not-found';
import { RenderCurrentStaticPage } from 'pages/static/render-current-static-page';
import { IStaticPageRoute } from 'remote/queries/static-page';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_LANGUAGE, PROD_SUPPORTED_LANGUAGES } from 'state/intl/constants';
import { usePageManagerContext } from 'state/static-page-manager';
import { useGetStaticPageByRoute } from 'state/static-page-manager/hooks/use-get-static-page-by-route';
import { isRedirectedToSimplexUrl } from 'utils/simplex';

import { StaticPageSkeleton } from './static-page.skeleton';

export const StaticPage = () => {
  const {
    currentPage,
    retrieveStaticPageById,
    routes,
    routesLoading,
    loadRoutes,
    loadRoutesHasBeenCalled,
  } = usePageManagerContext();
  const { language: currentLanguage, region } = useLocale();
  const { navigate } = useRouter();
  const isFocused = useIsFocused();

  useEffectOnce(() => {
    // If the loadRoutes query has not been called yet, load the static routes list
    if (!loadRoutesHasBeenCalled) {
      loadRoutes();
    }
  });

  const params = useGlobalSearchParams<{ lang: string }>();
  const pathname = usePathname();

  const { lang: routeLang } = params || {};

  // Remove first and last slash to check the "staticPath" and convert to lower case
  const staticPath = (pathname || '').replace(/^\/|\/$/g, '').toLowerCase();
  const routesAreLoading = !loadRoutesHasBeenCalled || routesLoading;

  const targetPage = useMemo(() => {
    let result: undefined | { route: IStaticPageRoute; language: string };
    if (routesAreLoading) {
      return;
    }

    routes.forEach(route => {
      if (routeLang) {
        if (route.path.current === staticPath) {
          result = { route, language: routeLang };
        }
      } else {
        const staticLocalizedPageLanguage = Object.keys(PROD_SUPPORTED_LANGUAGES).find(lang => {
          const localePath = route?.localePath;
          return localePath && lang in localePath && localePath[lang]?.current === staticPath;
        });

        if (staticLocalizedPageLanguage) {
          result = { route, language: staticLocalizedPageLanguage };
        }

        if (route.path.current === staticPath) {
          result = { route, language: PROD_DEFAULT_LANGUAGE[region] };
        }
      }
    });

    return result;
  }, [routes, routesAreLoading, staticPath, region, routeLang]);

  useEffect(() => {
    if (!targetPage) {
      return;
    }

    retrieveStaticPageById(
      targetPage.route._id,
      targetPage.route?.path?.current === appRoutes.nutritionExplorer.slice(1)
    );
  }, [targetPage, currentLanguage, retrieveStaticPageById, region]);

  const is404 = useMemo(() => !routesAreLoading && !targetPage, [routesAreLoading, targetPage]);
  const isTargetPageFetched = Boolean(targetPage && targetPage?.route?._id === currentPage?._id);

  const isClosingRef = useRef(false);
  const modalDismiss = useGetModalDismiss();
  const {
    modalRef,
    restoreOutsideElements,
    restoreFocusToTrigger,
    getModalA11yProps,
  } = useModalFocusTrap({
    // Only trap focus once real content has loaded, not the loading skeleton.
    active: isFocused && !routesAreLoading && !is404 && isTargetPageFetched,
    label: currentPage?.title ?? '',
    isClosingRef,
  });
  const handleDismiss = () => {
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    restoreOutsideElements();
    modalDismiss();
    restoreFocusToTrigger();
  };

  if (is404) {
    // If it is a SEO simplex URL navigate home.
    const fullPath = `/${staticPath}`;
    const wasRedirectedToSimplex = isRedirectedToSimplexUrl(fullPath);

    if (wasRedirectedToSimplex) {
      navigate(appRoutes.base);
      return (
        <BlankScreen
          context={{
            reason: 'Redirect to simple from static pages',
          }}
        />
      );
    }

    return <NotFound />;
  }

  if (!routesAreLoading && isTargetPageFetched && currentPage?.redirectUrl?.locale) {
    return <Redirect href={currentPage.redirectUrl.locale} />;
  }

  return (
    <View
      ref={modalRef}
      style={styles.modalFocusTrapWrapper}
      {...getModalA11yProps({ nativeRole: 'none' })}
    >
      <CardModalWithOptions
        key={staticPath}
        testID="static-page"
        helmetTitle={isTargetPageFetched ? currentPage?.title : undefined}
        onDismiss={handleDismiss}
      >
        {routesAreLoading || !currentPage ? (
          <LoyaltyLoader />
        ) : isTargetPageFetched ? (
          <RenderCurrentStaticPage currentPage={currentPage} />
        ) : (
          <StaticPageSkeleton />
        )}
      </CardModalWithOptions>
    </View>
  );
};

const styles = StyleSheet.create({
  embeddedPage: {
    flex: 1,
  },
  modalFocusTrapWrapper: {
    flex: 1,
  },
});

const CardModalWithOptions: CardModalProps = ({ children, ...props }) => {
  return (
    <CardModal
      alwaysShowHeader
      invertHeaderColor
      modalContainerStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      noDesktopWebMaxWidth // static pages have headers built into HTML and this looks weird.
      isFullScreen
      {...props}
    >
      {children}
    </CardModal>
  );
};

/**
 * This function takes a pathname as param and render a Static Page component that can be embedded
 * inside a page / component so we're not limited to using routes for static content.
 */
export const StaticPageEmbedded = ({
  pathname,
  currentPageOverrideCss,
}: {
  pathname: string;
  currentPageOverrideCss?: string;
}) => {
  const { currentPage, getStaticPageByRouteId, is404 } = useGetStaticPageByRoute();

  useEffect(() => {
    getStaticPageByRouteId(pathname);
  }, [getStaticPageByRouteId, pathname]);

  if (is404) {
    return <NotFound />;
  }

  return (
    <Box testID="static-page-embeded" style={styles.embeddedPage}>
      {currentPage ? (
        <RenderCurrentStaticPage
          currentPage={currentPage}
          currentPageOverrideCss={currentPageOverrideCss}
        />
      ) : (
        <LoadingContainer>
          <StaticPageSkeleton />
        </LoadingContainer>
      )}
    </Box>
  );
};

export default StaticPage;
